import React, { useEffect, useMemo, useState } from 'react';
import { InformationCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { clearOpenLeg } from '../../services/driverpay.service';

/**
 * Read-only drawer for Driver Pay - Open Legs (cms.driverpay_lineitem). Every field
 * is display-only — mirroring V1 editdriverpay_lineitem, which shows the leg's pay
 * line and offers a single "Clear Leg" action. Clear Leg blanks the driver / pay
 * status / approval / rate / fuel-type fields on the row (via /driverpay/open-legs/
 * {recno}/clear-leg) so the driver-pay process re-picks-up and re-rates the leg; it
 * is guarded by an inline confirm. The Rate Recno row flags a deleted / expired rate.
 */
const TABLE = 'driverpay-open-legs';
const s = (v: any): string => (v == null ? '' : String(v));

const Field: React.FC<{ label: string; children?: React.ReactNode; note?: React.ReactNode }> = ({ label, children, note }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between gap-2">
      <span>{(children == null || children === '') ? <span className="text-gray-400">—</span> : children}</span>
      {note}
    </div>
  </div>
);

const DriverPayOpenLegsDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const recno = row ? Number(row.recno) : 0;
  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [confirming, setConfirming] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (recno) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [recno]);

  const rateNote = useMemo(() => {
    if (s(record.ratedeleted) === 'Y') return '* DELETED *';
    if (s(record.expdate)) return `Expired ${s(record.expdate)}`;
    return null;
  }, [record]);

  const handleClear = async () => {
    if (!confirming) { setConfirming(true); return; }
    setIsClearing(true);
    setError(null);
    try {
      await clearOpenLeg(recno);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to clear leg.');
      setIsClearing(false);
      setConfirming(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        onClick={() => (confirming ? setConfirming(false) : onClose())}
        disabled={isClearing}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
      >
        {confirming ? 'Cancel' : 'Close'}
      </button>
      <button
        onClick={handleClear}
        disabled={isClearing}
        className="px-4 py-2 text-sm font-medium text-white bg-red-600 dark:bg-red-500 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 inline-flex items-center gap-2"
      >
        {isClearing ? (
          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Clearing…</>
        ) : (
          <><TrashIcon className="w-4 h-4" /> {confirming ? 'Confirm Clear Leg' : 'Clear Leg'}</>
        )}
      </button>
    </div>
  );

  return (
    <Drawer isOpen onClose={onClose} title={`Open Leg — Pro #${s(record.pronumber)}`} position="right" width="44rem" footer={footer}>
      <div className="space-y-5">
        {!!recno && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {confirming && (
          <div className="rounded-lg border border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/30 p-3 flex items-start gap-2">
            <InformationCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
            <p className="text-sm text-green-800 dark:text-green-300">
              This will reopen the leg allowing you to change the driver, location, or date.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Pro #">{s(record.pronumber)}</Field>
          <Field label="Leg #">{s(record.legnum)}</Field>
          <Field label="Driver">{s(record.driver)}</Field>
          <Field label="Event Date">{s(record.eventdate)}</Field>
          <Field label="Origin">{titleCase(s(record.origname))}</Field>
          <Field label="Destination">{titleCase(s(record.destname))}</Field>
          <Field label="Pay Status">{s(record.paystatus)}</Field>
          <Field label="Approved By">{s(record.approvedby)}</Field>
          <Field label="Approved Date">{s(record.approveddatetime)}</Field>
          <Field label="Payroll Date">{s(record.payrolldate)}</Field>
          <Field label="Rate">{s(record.rate)}</Field>
          <Field
            label="Rate Recno"
            note={rateNote && <span className="text-xs font-semibold text-red-600 dark:text-red-400 whitespace-nowrap">{rateNote}</span>}
          >
            {s(record.raterecno)}
          </Field>
          <Field label="Fuel Type">{s(record.fueltype)}</Field>
        </div>
      </div>
    </Drawer>
  );
};

export default DriverPayOpenLegsDrawer;
